import {
  AutocompleteInputChangeReason,
  Box,
  Button,
  Chip,
  Grid2 as Grid,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { FC, useMemo, useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { useLazyGetBanksQuery } from "src/shared/api/bank";
import { ContentAccordion } from "src/shared/components";
import AutocompleteFieldInner from "src/shared/components/fields/AutocompleteField/AutocompleteFieldInner";
import {
  EBankRequestField,
  IBankRequestParams,
  IBankResponseItem,
} from "src/shared/types/bank.types";
import { EAgencies, ECountries } from "src/shared/types/common.types";
import { EOrganizationValue } from "src/shared/types/companies.types";
import { IAutocompleteOption } from "src/shared/types/field.types";
import { messages } from "src/shared/utils/hookFormRules/messages";
import { getCurrentAccountRules } from "src/shared/utils/hookFormRules/rules";
import { IPartnerFormValues } from "../../model/types";
import { AddBankModal } from "../AddBankModal/AddBankModal";

interface IBanksSearchOptions {
  banks: Map<number, { address: string; bic: string; name: string }>;
  address: IAutocompleteOption<string>[];
  bic: IAutocompleteOption<string>[];
  name: IAutocompleteOption<string>[];
}

type TBankAccountType = "bank_account" | "correspondent_bank_account";
export const BankDetails: FC = () => {
  const [isAddBankModalOpen, setIsAddBankModalOpen] =
    useState<TBankAccountType | null>(null);
  const [banksSearchWasActivated, setBanksSearchWasActivated] = useState(false);
  const [options, setOptions] = useState<IBanksSearchOptions>({
    banks: new Map(),
    address: [],
    bic: [],
    name: [],
  });

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IPartnerFormValues>();
  const [getBanksRequest, { isFetching: isLoading }] = useLazyGetBanksQuery();

  const debouncedSearch = debounce((params) => getBanks(params), 1500);

  const getBanks = (params: IBankRequestParams) => {
    if (!params.filter || params.filter.length < 2) {
      return;
    }

    getBanksRequest(params)
      .unwrap()
      .then((response) => {
        const updatedOptions = response.reduce(
          (acc, bank) => {
            acc.banks.set(bank.id, {
              address: bank.address,
              bic: bank.bic,
              name: bank.name,
            });
            acc.address.push({
              name: bank.address,
              value: bank.address,
              extraValue: bank.id,
            });
            acc.bic.push({
              name: `${bank.bic} (${bank.address})`,
              value: bank.bic,
              extraValue: bank.id,
            });
            acc.name.push({
              name: `${bank.name} (${bank.address})`,
              value: bank.name,
              extraValue: bank.id,
            });

            return acc;
          },
          {
            banks: new Map(),
            address: [],
            bic: [],
            name: [],
          } as IBanksSearchOptions,
        );
        setOptions(updatedOptions);
        if (!banksSearchWasActivated) {
          setBanksSearchWasActivated(true);
        }
      })
      .catch((error) => {
        const errorMessage =
          error.originalStatus === 429
            ? "Слишком много запросов"
            : "Произошла ошибка";
        enqueueSnackbar(errorMessage, {
          variant: "error",
          handleClose: closeSnackbar,
        });
      });
  };

  const handleBankSelect = ({
    selectedBank,
    bank,
    field,
  }: {
    selectedBank: IAutocompleteOption<string> | null;
    bank?: IBankResponseItem;
    field: "bank_account" | "correspondent_bank_account";
  }) => {
    let bankData = undefined;
    let bankId = undefined;
    if (bank) {
      bankData = bank;
      bankId = bank.id;
    } else if (selectedBank) {
      bankId = selectedBank?.extraValue;
      bankData = bankId ? options.banks.get(Number(bankId)) : undefined;
    }
    if (field === "bank_account") {
      setValue("bank_account.bank_id", bankId ? Number(bankId) : "", {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        "bank_account.bank_address",
        bankData ? { name: bankData.address, value: bankData.address } : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        "bank_account.bank_bic",
        bankData ? { name: bankData.bic, value: bankData.bic } : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        "bank_account.bank_name",
        bankData ? { name: bankData.name, value: bankData.name } : null,
        { shouldValidate: true, shouldDirty: true },
      );
    }
    if (field === "correspondent_bank_account") {
      setValue(
        "bank_account.correspondent_bank_id",
        bankId ? Number(bankId) : "",
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );

      setValue(
        "bank_account.correspondent_bank_bic",
        bankData ? { name: bankData.bic, value: bankData.bic } : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        "bank_account.correspondent_bank_name",
        bankData ? { name: bankData.name, value: bankData.name } : null,
        { shouldValidate: true, shouldDirty: true },
      );
    }
  };

  const agency = useWatch({ name: "partner.agency", control });

  const selectInputChange = (
    reason: AutocompleteInputChangeReason,
    value: string,
    name: EBankRequestField,
  ) => {
    // if input change was fired by reset form or setValue
    // we don't need to make request
    if (reason !== "input") {
      return;
    }
    debouncedSearch({
      field: name,
      filter: value,
      agency: Number(agency),
    });
  };

  const registration_country_id = useWatch({
    control,
    name: "partner.registration_country_id",
  });

  const organization_form = useWatch({
    control,
    name: "partner.organization_form",
  });

  const isByRegistration = registration_country_id === ECountries.BY;
  const isRuRegistration = registration_country_id === ECountries.RU;
  const isNotByAndNotRuRegistration = !isByRegistration && !isRuRegistration;

  const currentAccountRules = useMemo(
    () =>
      getCurrentAccountRules({
        country_id: registration_country_id,
        organization_form,
      }),
    [registration_country_id, organization_form],
  );

  const hasErrors = errors.bank_account;

  const showAddBankButton =
    isNotByAndNotRuRegistration && banksSearchWasActivated;

  const noOptionsText = useMemo(() => {
    return (
      <Stack spacing={2}>
        <Typography>Ничего не найдено</Typography>
        {showAddBankButton ? (
          <Button
            variant="contained"
            onClick={() => setIsAddBankModalOpen("bank_account")}
          >
            Добавить банк
          </Button>
        ) : null}
      </Stack>
    );
  }, [showAddBankButton]);

  return (
    <ContentAccordion
      title={
        <Box display="flex" alignItems="center">
          <Chip label="3" />
          <Typography
            color={hasErrors ? "error" : "grey.500"}
            sx={{ ml: 2 }}
            component="span"
          >
            Банковские реквизиты
          </Typography>
        </Box>
      }
    >
      <AddBankModal
        agency={agency || EAgencies.Webcom_BY}
        onSuccess={(bank) =>
          handleBankSelect({
            selectedBank: null,
            bank,
            field: isAddBankModalOpen || "bank_account",
          })
        }
        open={!!isAddBankModalOpen}
        onClose={() => setIsAddBankModalOpen(null)}
      />
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Stack spacing={4}>
            <Controller
              control={control}
              name="bank_account.current_account"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  fullWidth
                  {...field}
                  label={
                    isByRegistration ? "Расчетный счет/IBAN" : "Расчетный счет"
                  }
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              rules={currentAccountRules}
            />

            {isByRegistration && (
              <Controller
                control={control}
                name="bank_account.transit_account"
                rules={{
                  validate: (value) =>
                    !value ||
                    value.length === 28 ||
                    "Транзитный счет должен содержать 28 символов",
                }}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Транзитный счет"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          </Stack>
        </Grid>
        {!isByRegistration && (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Controller
              control={control}
              name="bank_account.correspondent_account"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  fullWidth
                  {...field}
                  label="Корреспондентский счет"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              rules={{
                pattern: isRuRegistration
                  ? {
                      value: /^[0-9]{0,20}$/,
                      message: "0 - 20 цифр",
                    }
                  : {
                      value: /^[a-zA-Z0-9]{20}$/,
                      message: "20 символов [Aa-Zz 0-9]",
                    },
              }}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="bank_account.bank_bic"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <AutocompleteFieldInner<string>
                loading={isLoading}
                noOptionsText={noOptionsText}
                placeholder="Минимум 2 символа"
                onInputChange={(_e, v, reason) => {
                  selectInputChange(reason, v, EBankRequestField.bic);
                }}
                inputRef={ref}
                fullWidth
                {...field}
                label={isNotByAndNotRuRegistration ? "БИК (SWIFT)" : "БИК"}
                error={!!error}
                helperText={error?.message}
                options={options.bic}
                onChange={(_e, v) =>
                  handleBankSelect({
                    selectedBank: v as IAutocompleteOption,
                    field: "bank_account",
                  })
                }
                slotProps={{
                  paper: {
                    sx: { minWidth: showAddBankButton ? 230 : undefined },
                  },
                }}
              />
            )}
            rules={{
              required:
                isNotByAndNotRuRegistration &&
                organization_form === EOrganizationValue.LegalEntity
                  ? messages.required
                  : undefined,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Controller
            control={control}
            name="bank_account.bank_name"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <AutocompleteFieldInner<string>
                loading={isLoading}
                noOptionsText={noOptionsText}
                placeholder="Минимум 2 символа"
                onInputChange={(_e, v, reason) => {
                  selectInputChange(reason, v, EBankRequestField.bank_name);
                }}
                inputRef={ref}
                fullWidth
                {...field}
                label={
                  isNotByAndNotRuRegistration
                    ? "Наименование банка / Bank name"
                    : "Наименование"
                }
                error={!!error}
                helperText={error?.message}
                options={options.name}
                onChange={(_e, v) =>
                  handleBankSelect({
                    selectedBank: v as IAutocompleteOption,
                    field: "bank_account",
                  })
                }
                slotProps={{
                  paper: {
                    sx: { minWidth: showAddBankButton ? 230 : undefined },
                  },
                }}
              />
            )}
            rules={{
              required:
                !isNotByAndNotRuRegistration &&
                organization_form === EOrganizationValue.LegalEntity
                  ? messages.required
                  : undefined,
            }}
          />
          <Controller
            control={control}
            name="bank_account.bank_id"
            render={({ field }) => <input type="hidden" {...field} />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="bank_account.bank_address"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <AutocompleteFieldInner<string>
                loading={isLoading}
                noOptionsText={noOptionsText}
                placeholder="Минимум 2 символа"
                onInputChange={(_e, v, reason) => {
                  selectInputChange(reason, v, EBankRequestField.address);
                }}
                inputRef={ref}
                fullWidth
                {...field}
                label={
                  isNotByAndNotRuRegistration ? "Адрес / Bank address" : "Адрес"
                }
                error={!!error}
                helperText={error?.message}
                options={options.address}
                onChange={(_e, v) =>
                  handleBankSelect({
                    selectedBank: v as IAutocompleteOption,
                    field: "bank_account",
                  })
                }
                slotProps={{
                  paper: {
                    sx: { minWidth: showAddBankButton ? 230 : undefined },
                  },
                }}
              />
            )}
            rules={{
              required:
                !isNotByAndNotRuRegistration &&
                organization_form === EOrganizationValue.LegalEntity
                  ? messages.required
                  : undefined,
            }}
          />
        </Grid>

        {isNotByAndNotRuRegistration && (
          <>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <Controller
                control={control}
                name="bank_account.correspondent_bank_bic"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <AutocompleteFieldInner<string>
                    loading={isLoading}
                    noOptionsText={noOptionsText}
                    placeholder="Минимум 2 символа"
                    onInputChange={(_e, v, reason) => {
                      selectInputChange(reason, v, EBankRequestField.bic);
                    }}
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label={"БИК (SWIFT) банка-корреспондента"}
                    error={!!error}
                    helperText={error?.message}
                    options={options.bic}
                    onChange={(_e, v) =>
                      handleBankSelect({
                        selectedBank: v as IAutocompleteOption,
                        field: "correspondent_bank_account",
                      })
                    }
                    slotProps={{
                      paper: {
                        sx: { minWidth: showAddBankButton ? 230 : undefined },
                      },
                    }}
                  />
                )}
                rules={{
                  required:
                    !isNotByAndNotRuRegistration &&
                    organization_form === EOrganizationValue.LegalEntity
                      ? messages.required
                      : undefined,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Controller
                control={control}
                name="bank_account.correspondent_bank_name"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <AutocompleteFieldInner<string>
                    loading={isLoading}
                    noOptionsText={noOptionsText}
                    placeholder="Минимум 2 символа"
                    onInputChange={(_e, v, reason) => {
                      selectInputChange(reason, v, EBankRequestField.bank_name);
                    }}
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label={"Наименование банка-корреспондента"}
                    error={!!error}
                    helperText={error?.message}
                    options={options.name}
                    onChange={(_e, v) =>
                      handleBankSelect({
                        selectedBank: v as IAutocompleteOption,
                        field: "correspondent_bank_account",
                      })
                    }
                    slotProps={{
                      paper: {
                        sx: { minWidth: showAddBankButton ? 230 : undefined },
                      },
                    }}
                  />
                )}
                rules={{
                  required:
                    !isNotByAndNotRuRegistration &&
                    organization_form === EOrganizationValue.LegalEntity
                      ? messages.required
                      : undefined,
                }}
              />
              <Controller
                control={control}
                name="bank_account.correspondent_bank_id"
                render={({ field }) => <input type="hidden" {...field} />}
              />
            </Grid>
          </>
        )}
      </Grid>
    </ContentAccordion>
  );
};
